'use client';

import { StoryblokReactComponent } from '@/storyblok/components/componentsMap';
import { PostChecklistStoryblok } from '@/storyblok/types';
import { generateServiceLocationUrl } from '@/routes';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { ArrowRightIcon, CheckIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useRouter } from 'next/navigation';
import { render } from 'storyblok-rich-text-react-renderer';
import { makeRichTextRenderOptions } from '@/storyblok/utils/richText';

export const PostChecklist: StoryblokReactComponent<PostChecklistStoryblok> = ({ story, component }) => {
  const router = useRouter();

  return (
    <div className="bg-card overflow-hidden rounded-2xl border-2 border-primary/15 shadow-card">
      <div className="flex items-center gap-3 bg-primary px-4 py-3.5 text-white text-white sm:px-6 sm:py-4">
        <QuestionMarkCircleIcon className="size-7 shrink-0 sm:size-8" />

        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold uppercase tracking-widest text-white/70">{component.title}</div>
          <h4 className="text-sm font-bold leading-tight sm:text-base">{component.subtitle}</h4>
        </div>
      </div>

      <div className="p-5 sm:p-6 md:p-7">
        {component.description && <p className="mb-4 text-sm leading-relaxed text-neutral-lighter sm:mb-5">{component.description}</p>}

        <ul className="grid list-none gap-x-6 gap-y-3 pl-0 sm:grid-cols-2 sm:gap-y-4">
          {component.items?.map(item => (
            <li key={item._uid} className="list-none !pl-0 before:!hidden">
              <label className="-mx-2 flex cursor-pointer select-none items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-primary-active/5">
                <input type="checkbox" className="peer sr-only" />
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-primary/40 bg-white transition-colors peer-checked:bg-primary">
                  <CheckIcon className="size-3 stroke-2 text-white" />
                </span>

                <div className="text-sm leading-snug text-neutral-darker transition-colors peer-checked:font-semibold">
                  {render(item.text, makeRichTextRenderOptions(story))}
                </div>
              </label>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex flex-col sm:mt-6">
          <ELButton
            label="Prenota una visita"
            variant="floating"
            color="primary"
            trailingIcon={<ArrowRightIcon className="size-4 stroke-2" />}
            size="small"
            className="text-primary-white mx-auto h-11 w-full shrink-0 rounded-full bg-primary px-5 text-sm text-white transition-all hover:bg-primary-hover sm:w-auto sm:px-12 [&>div]:gap-2 [&_span]:font-semibold"
            onClick={() => router.push(generateServiceLocationUrl('treatment', { service: component.speciality }))}
          />

          <p className="mt-5 flex flex-col gap-4 border-t border-primary/15 pt-4 text-justify text-xs leading-snug text-neutral-lighter sm:mt-6 sm:flex-row sm:items-center sm:justify-between sm:pt-5">
            Questo servizio è indicato per sintomi non urgenti. Se i sintomi sono intensi, improvvisi, in peggioramento rapido o associati a
            difficoltà respiratoria, dolore toracico, perdita di coscienza, deficit neurologici, sanguinamento importante o febbre alta
            persistente, non attendere una visita online: contatta il 112/118, vai in Pronto Soccorso o rivolgiti subito al tuo medico.
          </p>
        </div>
      </div>
    </div>
  );
};
